'use client'

import { isNull, isUndefined } from 'lodash-es'
import {
  type RefObject,
  startTransition,
  useCallback,
  useEffect,
  useRef,
} from 'react'

const SNAP_DURATION_MS = 280
/** 제자리로 돌아갈 때 애니메이션 duration (스냅보다 느리게) */
const SNAP_BACK_DURATION_MS = 420
/** 드래그가 이 비율 이상이면 스냅, 미만이면 터치 시작 위치로 복귀 */
const SNAP_THRESHOLD = 0.4
/** 필터에서 터치 시 가로/세로 방향 결정을 위한 최소 이동 거리(px) */
const GESTURE_LOCK_THRESHOLD = 10
/** 손을 뗄 때 이 속도(px/ms) 이상이면 거리와 관계없이 스냅 (세게 밀어서 뗀 경우) */
const FLING_VELOCITY_THRESHOLD = 0.5

const SNAP_EASING = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)'

/** translate3d + 정수 px로 컴포지터 활용·서브픽셀 비용 감소 */
const setSheetTransform = (sheet: HTMLDivElement, translateY: number) => {
  const px = Math.round(translateY)
  sheet.style.transform = `translate3d(0,${px}px,0)`
}

type Direction = 'up' | 'down' | null

type GestureState =
  | { phase: 'idle' }
  | {
      phase: 'started' // touchstart 직후. 아직 시트 이동 여부 미결정
      startSheetY: number
      startTouchX: number
      startTouchY: number
      startedOnFilter: boolean
      /** 필터 터치일 때만 의미 있음. null = 방향 미결정 */
      gestureLock: 'horizontal' | 'vertical' | null
      prevY: number
      prevTime: number
      lastVelocity: number
      direction: Direction
    }
  | { phase: 'filter-scrolling' } // 가로 잠금 확정 → 시트는 이 제스처 무시
  | {
      phase: 'dragging' // 시트 드래그 확정 (이후 canUserMoveSheet 체크 불필요)
      startSheetY: number
      startTouchY: number
      prevY: number
      prevTime: number
      lastVelocity: number
      direction: Direction
    }

/** 손을 뗄 때 스냅할 위치(0 | maxY | startY)를 결정 */
function resolveSnapPoint(params: {
  direction: Direction
  lastVelocity: number
  content: HTMLElement | null
  sheetY: number
  maxY: number
  startY: number
}): number | null {
  const { direction, lastVelocity, content, sheetY, maxY, startY } = params

  const isDraggingDown =
    direction === 'down' && content !== null && content.scrollTop <= 0
  const isDraggingUp = direction === 'up'

  const isFlingDown = isDraggingDown && lastVelocity > FLING_VELOCITY_THRESHOLD
  const isFlingUp = isDraggingUp && lastVelocity < -FLING_VELOCITY_THRESHOLD

  const distanceToCollapsed = maxY - startY
  const distanceDraggedTowardCollapsed = sheetY - startY
  const draggedEnoughToCollapse =
    distanceToCollapsed <= 0 ||
    distanceDraggedTowardCollapsed >= distanceToCollapsed * SNAP_THRESHOLD

  const distanceToExpanded = startY
  const distanceDraggedTowardExpanded = startY - sheetY
  const draggedEnoughToExpand =
    distanceToExpanded <= 0 ||
    distanceDraggedTowardExpanded >= distanceToExpanded * SNAP_THRESHOLD

  if (isDraggingDown) {
    const shouldCollapse = isFlingDown || draggedEnoughToCollapse

    return shouldCollapse ? maxY : startY
  }

  if (isDraggingUp) {
    const shouldExpand = isFlingUp || draggedEnoughToExpand

    return shouldExpand ? 0 : startY
  }

  return null
}

type Params = {
  sheetRef: RefObject<HTMLDivElement | null>
  contentRef: RefObject<HTMLElement | null>
  filterRef: RefObject<HTMLElement | null> | undefined
  maxSheetYRef: RefObject<number>
  currentSheetYRef: RefObject<number>
  defaultCollapsedHeight: number | undefined
  /** 시트가 초기 기본 위치 상태인지 설정 */
  setAtDefaultPosition: (value: boolean) => void
  setCollapsed: (value: boolean) => void
  onCollapsedChange: ((collapsed: boolean) => void) | undefined
}

export const useSheetGesture = ({
  sheetRef,
  contentRef,
  filterRef,
  maxSheetYRef,
  currentSheetYRef,
  defaultCollapsedHeight,
  setAtDefaultPosition,
  setCollapsed,
  onCollapsedChange,
}: Params) => {
  const gestureRef = useRef<GestureState>({ phase: 'idle' })

  useEffect(() => {
    const sheet = sheetRef.current
    if (!sheet) return

    /**
     * 시트를 드래그할 수 있는지 여부.
     * 필터 gesture lock 체크는 상태 머신이 담당하므로 여기서는 제외.
     */
    const canUserMoveSheet = (direction: Direction): boolean => {
      const content = contentRef.current
      if (!content) return false

      const listIsScrolled = content.scrollTop > 0
      const sheetIsPartiallyCollapsed = currentSheetYRef.current > 0
      const isDraggingDownOnFullyOpenSheet =
        direction === 'down' && content.scrollTop <= 0

      if (listIsScrolled) return false

      if (sheetIsPartiallyCollapsed || isDraggingDownOnFullyOpenSheet)
        return true

      return false
    }

    const handleTouchStart = (event: TouchEvent) => {
      // 진행 중인 transition이 있으면 현재 위치에서 즉시 멈춤
      sheet.style.transition = ''
      const currentTransform = new WebKitCSSMatrix(
        getComputedStyle(sheet).transform,
      )
      currentSheetYRef.current = currentTransform.m42 // translateY 값
      setSheetTransform(sheet, currentSheetYRef.current)

      const [touch] = event.touches
      const startedOnFilter = !!filterRef?.current?.contains(
        event.target as Node,
      )

      gestureRef.current = {
        phase: 'started',
        startSheetY: currentSheetYRef.current,
        startTouchX: touch.clientX,
        startTouchY: touch.clientY,
        startedOnFilter,
        gestureLock: null,
        prevY: touch.clientY,
        prevTime: event.timeStamp,
        lastVelocity: 0,
        direction: null,
      }
    }

    const handleTouchMove = (event: TouchEvent) => {
      const gesture = gestureRef.current

      // filter-scrolling은 필터 자체가 처리하므로 시트는 무시
      if (gesture.phase === 'idle' || gesture.phase === 'filter-scrolling')
        return

      const [touch] = event.touches
      const currentX = touch.clientX
      const currentY = touch.clientY
      const now = event.timeStamp

      // 속도·방향은 prev 갱신 전에 계산해야 정확함 (갱신 후 비교하면 0이 됨)
      const dt = now - gesture.prevTime
      if (dt > 0) gesture.lastVelocity = (currentY - gesture.prevY) / dt
      if (currentY > gesture.prevY) gesture.direction = 'down'
      if (currentY < gesture.prevY) gesture.direction = 'up'
      gesture.prevY = currentY
      gesture.prevTime = now

      // 필터에서 시작한 터치: 방향이 아직 정해지지 않았으면 최소 이동량으로 가로/세로 잠금
      if (gesture.phase === 'started' && gesture.startedOnFilter) {
        if (gesture.gestureLock === null) {
          const deltaX = Math.abs(currentX - gesture.startTouchX)
          const deltaY = Math.abs(currentY - gesture.startTouchY)
          if (Math.max(deltaX, deltaY) >= GESTURE_LOCK_THRESHOLD) {
            gesture.gestureLock = deltaX > deltaY ? 'horizontal' : 'vertical'
          }
        }

        // 가로로 잠긴 경우 → filter-scrolling으로 전환, 시트는 건드리지 않음
        if (gesture.gestureLock === 'horizontal') {
          gestureRef.current = { phase: 'filter-scrolling' }

          return
        }
      }

      // 아직 드래그 미확정 → 시트를 이동할 수 있는지 판단
      if (gesture.phase === 'started') {
        if (!canUserMoveSheet(gesture.direction)) return

        // 드래그 확정: started → dragging
        gestureRef.current = {
          phase: 'dragging',
          startSheetY: gesture.startSheetY,
          startTouchY: gesture.startTouchY,
          prevY: currentY,
          prevTime: now,
          lastVelocity: gesture.lastVelocity,
          direction: gesture.direction,
        }
      }

      // dragging 단계: 시트 이동
      const dragging = gestureRef.current as Extract<
        GestureState,
        { phase: 'dragging' }
      >

      if (event.cancelable) event.preventDefault()
      event.stopPropagation()
      sheet.style.transition = 'none'

      const touchOffset = currentY - dragging.startTouchY
      const maxY = maxSheetYRef.current
      const nextSheetY = Math.min(
        Math.max(dragging.startSheetY + touchOffset, 0),
        maxY,
      )

      setSheetTransform(sheet, nextSheetY)
      currentSheetYRef.current = nextSheetY
    }

    const handleTouchEnd = () => {
      const gesture = gestureRef.current
      if (gesture.phase === 'idle') return

      // 즉시 리셋 (다음 touchstart 전에 idle 상태로)
      gestureRef.current = { phase: 'idle' }

      // 필터 가로 스크롤이었으면 시트 스냅 불필요
      if (gesture.phase === 'filter-scrolling') return

      sheet.style.transition = ''

      const sheetY = currentSheetYRef.current
      const maxY = maxSheetYRef.current
      const startY = gesture.startSheetY

      if (sheetY <= 0) {
        setAtDefaultPosition(false)
        setCollapsed(false)
        onCollapsedChange?.(false)

        return
      }

      const snapPoint = resolveSnapPoint({
        direction: gesture.direction,
        lastVelocity: gesture.lastVelocity,
        content: contentRef.current,
        sheetY,
        maxY,
        startY,
      })

      if (isNull(snapPoint)) return

      const isSnapBack = snapPoint === startY
      const durationMs = isSnapBack ? SNAP_BACK_DURATION_MS : SNAP_DURATION_MS
      sheet.style.transition = `transform ${durationMs}ms ${SNAP_EASING}`

      setSheetTransform(sheet, snapPoint)
      currentSheetYRef.current = snapPoint

      const newCollapsed = snapPoint === maxY

      // 완전히 열리거나 닫히는 경우에만 초기 위치 상태 해제 (제자리 복귀는 유지)
      if (!isSnapBack) {
        setAtDefaultPosition(false)
      }

      // 터치 직후 리플로우/리페인트가 UI를 막지 않도록 함
      startTransition(() => {
        setCollapsed(newCollapsed)
        onCollapsedChange?.(newCollapsed)
      })

      sheet.addEventListener(
        'transitionend',
        () => {
          sheet.style.transition = ''
        },
        { once: true },
      )
    }

    sheet.addEventListener('touchstart', handleTouchStart, {
      passive: true,
      capture: true,
    })
    sheet.addEventListener('touchmove', handleTouchMove, {
      passive: false,
      capture: true,
    })
    sheet.addEventListener('touchend', handleTouchEnd, {
      passive: true,
      capture: true,
    })

    return () => {
      sheet.removeEventListener('touchstart', handleTouchStart, true)
      sheet.removeEventListener('touchmove', handleTouchMove, true)
      sheet.removeEventListener('touchend', handleTouchEnd, true)
    }
  }, [contentRef, filterRef, onCollapsedChange])

  const collapse = useCallback(() => {
    const sheet = sheetRef.current
    if (!sheet) return

    const maxY = maxSheetYRef.current
    sheet.style.transition = `transform ${SNAP_DURATION_MS}ms ${SNAP_EASING}`
    setSheetTransform(sheet, maxY)
    currentSheetYRef.current = maxY
    setCollapsed(true)

    sheet.addEventListener(
      'transitionend',
      () => {
        sheet.style.transition = ''
        onCollapsedChange?.(true)
      },
      { once: true },
    )
  }, [onCollapsedChange])

  const snapToDefaultHeight = useCallback(() => {
    const sheet = sheetRef.current
    if (!sheet) return

    const maxY = maxSheetYRef.current
    const { height } = sheet.getBoundingClientRect()
    const targetY = isUndefined(defaultCollapsedHeight)
      ? maxY
      : Math.max(height - defaultCollapsedHeight, 0)

    sheet.style.transition = `transform ${SNAP_DURATION_MS}ms ${SNAP_EASING}`
    setSheetTransform(sheet, targetY)
    currentSheetYRef.current = targetY

    setAtDefaultPosition(targetY !== 0 && targetY !== maxY)
    setCollapsed(false)

    sheet.addEventListener(
      'transitionend',
      () => {
        sheet.style.transition = ''
        onCollapsedChange?.(false)
      },
      { once: true },
    )
  }, [
    currentSheetYRef,
    defaultCollapsedHeight,
    maxSheetYRef,
    onCollapsedChange,
    setAtDefaultPosition,
    setCollapsed,
    sheetRef,
  ])

  return { collapse, snapToDefaultHeight }
}
